/**
 * @file Dashboard service.
 * @description Fetches dashboard KPIs/charts and upload-export history with resilient fallbacks.
 * @author Sanjay Jangid (Jiggle frontend)
 * @email sanjay.jangid@dgtoohl.com
 * @date 2026-05-10
 * @version 0.0.0
 */
import { apiFetch } from '../api/api';
import { buildQueryString } from '../utils/api';
import { API_ENDPOINTS } from '../constants/api';
import { DashboardData, HistoryResponse, DashboardApiResponse, HistoryApiResponse } from '../types';

/**
 * Dashboard Service
 * Handles all dashboard-related API operations
 */
export class DashboardService {
  /**
   * Fetch dashboard data including KPIs and charts
   */
  static async getDashboardData(): Promise<DashboardData> {
    const params = {
      id: '653456',
      key: 'wrtretvwetwertvwe',
    };

    const queryString = buildQueryString(params);
    const endpointWithQuery = `${API_ENDPOINTS.DASHBOARD}?${queryString}`;

    try {
      const response = await apiFetch<DashboardApiResponse>(endpointWithQuery, 'GET');
      return response.data;
    } catch (error) {
      throw error instanceof Error ? error : new Error('Failed to fetch dashboard data');
    }
  }

  /**
   * Fetch upload and export history
   */
  static async getHistory(): Promise<HistoryResponse> {
    try {
      const response = await apiFetch<HistoryApiResponse>(
        API_ENDPOINTS.HISTORY,
        'GET',
      );
      return response.data;
    } catch (error) {
      throw error instanceof Error ? error : new Error('Failed to fetch dashboard history');
    }
  }

  /**
   * Fetch both dashboard data and history in parallel
   */
  static async getDashboardDataWithHistory(): Promise<{
    dashboard: DashboardData;
    history: HistoryResponse;
  }> {
    const [dashboard, history] = await Promise.all([
      this.getDashboardData(),
      this.getHistory(),
    ]);

    return { dashboard, history };
  }
}
